<template>
    <div
        class="focus-within:bg-gray-100 focus-within:outline-none focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-500 group flex justify-between items-center rounded-lg transition duration-150 my-1 | hover:bg-gray-100"
    >
        <button
            class="focus:outline-none flex flex-1 items-center min-w-0 text-left rounded-lg transition duration-150 py-3 px-2 cursor-pointer"
            data-testid="search-bar-item"
            type="button"
            @click.stop="$emit('click-item', searchItem, false)"
        >
            <div class="relative mr-3 shrink-0">
                <div
                    class="w-10 h-10 bg-gray-200 transition duration-150 group-hover:bg-gray-250 rounded-full flex items-center justify-center"
                >
                    <activix-avatar
                        :size="27"
                        class="bg-gray-200 rounded-full transition duration-150 text-gray-700 group-hover:bg-gray-250"
                        :username="searchItem.title"
                        v-if="searchItem.type === 'user'"
                    />
                    <icon :name="getIcon" class="w-7 text-gray-700" v-else />
                </div>
                <icon
                    class="absolute -bottom-0.5 -right-0.5 text-sm bg-white rounded-full pointer-events-none"
                    :class="statusIconClass"
                    :name="statusIcon"
                    v-if="showStatusIcon"
                />
            </div>

            <div class="flex flex-col min-w-0">
                <div class="flex items-baseline space-x-2 min-w-0">
                    <div
                        class="font-semibold text-md text-gray-700 group-hover:text-gray-800 truncate"
                    >
                        {{ searchItem.title?.trim() ? searchItem.title : $t('clientCard.unknown') }}
                    </div>
                    <activix-tooltip
                        :content="$t('history.lead.createdAt')"
                        placement="bottom"
                        v-if="createdAtFormat"
                    >
                        <div class="text-sm text-gray-600 shrink-0">
                            {{ createdAtFormat }}
                        </div>
                    </activix-tooltip>
                </div>
                <div class="text-sm space-x-1 text-gray-600">
                    <span v-if="searchItem.subTitle">
                        {{ searchItem.subTitle }}
                    </span>
                    <span v-if="searchItem.description">
                        {{ searchItem.description }}
                    </span>
                </div>
                <div class="text-sm text-gray-550" v-if="searchItem.other">
                    {{ searchItem.other }}
                </div>
            </div>
        </button>
        <div class="hidden group-hover:flex group-focus-within:flex space-x-1 items-center pr-2">
            <activix-tooltip placement="bottom" :content="$t('modal.newTab')">
                <button
                    class="p-1 text-grey-600 duration-150 transition hover:text-gray-800"
                    data-testid="search-bar-item-open-new-tab"
                    tabindex="-1"
                    type="button"
                    @click.stop="$emit('click-item', searchItem, true)"
                >
                    <icon name="regular/expand-6" />
                </button>
            </activix-tooltip>
            <activix-tooltip placement="bottom" :content="$t('search.delete')" v-if="clearable">
                <button
                    class="p-1 text-grey-600 duration-150 transition hover:text-gray-800"
                    data-testid="search-bar-item-clear"
                    tabindex="-1"
                    type="button"
                    @click.stop="$emit('clear', searchItem)"
                >
                    <icon name="bold/close" class="text-sm mt-0.5" />
                </button>
            </activix-tooltip>
        </div>
    </div>
</template>

<script lang="ts">
    import ActivixDate from '@/value-objects/ActivixDate.js';
    import LeadStatus from '@/entities/LeadStatus.js';
    import { SearchBarItemType, SearchBarModelType } from '@/entities/SearchBar.ts';
    import type { SearchBarItemObj } from '@/entities/SearchBar.ts';

    export default {
        emits: ['clear', 'click-item'],

        props: {
            searchItem: {
                type: Object as () => SearchBarItemObj,
                required: true,
            },
            clearable: {
                type: Boolean,
                default: false,
            },
        },

        computed: {
            createdAtFormat(): string {
                if (!this.searchItem.createdAt) return '';
                const date = new ActivixDate(this.searchItem.createdAt);

                return date.toHumanShort(false);
            },

            showStatusIcon(): boolean {
                return (
                    [SearchBarModelType.LEAD, SearchBarModelType.RECENT].includes(
                        this.searchItem.type
                    ) && this.searchItem.status !== undefined
                );
            },

            statusIcon(): string {
                return LeadStatus.getIcon(this.searchItem.status);
            },

            statusIconClass(): string {
                return (
                    (
                        {
                            [LeadStatus.INVALID]: 'link-orange',
                            [LeadStatus.DUPLICATE]: 'link-gray',
                            [LeadStatus.LOST]: 'link-red',
                        } as Record<string, string>
                    )[this.searchItem.status ?? ''] || 'link-green'
                );
            },

            getIcon(): string {
                if (this.searchItem.itemType === SearchBarItemType.SEARCH) {
                    return 'regular/search';
                }

                if (this.searchItem.type === SearchBarModelType.NOTE) {
                    return 'regular/notes-paper-text';
                }

                if (this.searchItem.type === SearchBarModelType.VEHICLE) {
                    return 'regular/car-1';
                }

                if (this.searchItem.type === SearchBarModelType.CUSTOMER) {
                    return 'regular/single-neutral';
                }

                return this.searchItem.icon ?? 'regular/search';
            },
        },
    };
</script>
